import React, { useState, useEffect } from "react";
import { useSearchParams, Navigate, useLocation } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

import withErrorHandling from "../HOC/ErrorHandling";

import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";

import {
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

import t from "../helpers/en";
import { authWithCustomLogin, authWithGoogle, associateCliCode } from "../helpers/utils";
import { hasValidToken, getUserData } from "../helpers/tokenStorage";
import Loading from "../components/Loading";

const SIMPLE_LOGIN = "SIMPLE_LOGIN";
const GOOGLE_LOGIN = "GOOGLE_LOGIN";

const SuccessView = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: '2rem',
      padding: '2rem',
    }}
  >
    <img
      style={{ width: "7em", margin: "0 auto" }}
      src={process.env.PUBLIC_URL + "/logo.png"}
      alt="logo"
    />
    <CheckCircleIcon 
      sx={{ 
        fontSize: 64,
        color: 'success.main'
      }} 
    />
    <Typography variant="h5" color="success.main" gutterBottom>
      Authentication Successful!
    </Typography>
    <Typography color="text.secondary">
      Your CLI application has been authenticated successfully. You can now close this window and return to your terminal.
    </Typography>
  </Box>
);

const CliLogin = (props) => {
  const { showError } = props;
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    username: "",
    password: "",
  });
  const [success, setSuccess] = useState(false);

  // Get the auth code from URL parameters
  const authCode = searchParams.get("code");

  useEffect(() => {
    if (!authCode) {
      showError("Missing authentication code");
    }
  }, [authCode, showError]);

  const onChangeTxt = (evt) => {
    const { name, value } = evt.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const onLogin = (btnName, credentials) => {
    if (btnName === SIMPLE_LOGIN) {
      let newErrors = { username: "", password: "" };
      if (!form.username) {
        newErrors.username = "Username is required";
      }
      if (!form.password) {
        newErrors.password = "Password is required";
      }
      if (newErrors.username || newErrors.password) {
        setErrors(newErrors);
        showError("Please fill in all required fields");
        return;
      }
      setErrors({ username: "", password: "" });
    }

    setLoading(true);

    // Add the auth code to the credentials
    const enrichedCredentials = {
      ...credentials,
      authCode: authCode
    };

    // SCK-725: /google and /local mint the portal JWT but do NOT associate
    // the CLI's authCode with it — the CLI's poll loop would then time out
    // waiting for a code that was never stored. Chain the dedicated
    // /associate-cli-code call on success so the first `c2a login` attempt
    // just works, instead of requiring the user to re-run the command.
    const finishWithAssociation = (loginErr) => {
      if (loginErr) {
        setLoading(false);
        showError(loginErr);
        return;
      }
      if (!authCode) {
        // Defensive: the useEffect above already showed an error if the
        // code is missing. Just mark success for the portal-login half.
        setLoading(false);
        setSuccess(true);
        return;
      }
      associateCliCode(authCode, (_result, assocStatus, assocErr) => {
        setLoading(false);
        if (assocErr || assocStatus !== 200) {
          showError(
            assocErr ||
              "CLI association failed. Try running `c2a login` again."
          );
          return;
        }
        setSuccess(true);
      });
    };

    if (btnName === GOOGLE_LOGIN) {
      authWithGoogle(enrichedCredentials, (_response, _status, err) => {
        finishWithAssociation(err);
      });
    } else {
      authWithCustomLogin(enrichedCredentials, (_result, _status, err) => {
        finishWithAssociation(err);
      });
    }
  };

  const onToggleShowPassword = () => setShowPassword(show => !show);

  const onKeyPress = (evt) => {
    if (evt.key === "Enter") {
      evt.preventDefault();
      onLogin(SIMPLE_LOGIN, form);
    }
  };

  // If the user already has a valid session AND arrived here to
  // authenticate a CLI, don't redirect them away — that used to strand
  // the CLI polling forever for a code that was never associated
  // (see /api/auth/associate-cli-code). Only redirect if no authCode
  // is present (i.e. the user landed here directly, not from `c2a login`).
  if (hasValidToken() && !authCode) {
    return <Navigate to={location?.state?.from?.pathname ?? "/"} replace />;
  }

  const onConfirmAssociation = () => {
    if (!authCode) return;
    setLoading(true);
    associateCliCode(authCode, (result, status, err) => {
      setLoading(false);
      if (!err && status === 200) {
        setSuccess(true);
      } else {
        showError(err || "Could not authenticate CLI. Try signing out and running `c2a login` again.");
      }
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "2rem",
        width: "100%",
        maxWidth: "400px",
        margin: "0 auto",
        gap: "1.5rem",
      }}
    >
      {success ? (
        <SuccessView />
      ) : hasValidToken() && authCode ? (
        <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <img
            style={{ width: "7em", margin: "0 auto" }}
            src={process.env.PUBLIC_URL + "/logo.png"}
            alt="logo"
          />
          <Typography variant="h5">Authenticate CLI</Typography>
          <Typography color="text.secondary">
            You're signed in
            {getUserData()?.email ? ` as ${getUserData().email}` : ""}.
            Authorize your <code>c2a</code> CLI session using this browser login?
          </Typography>
          <Button
            variant="contained"
            onClick={onConfirmAssociation}
            disabled={loading}
            sx={{ mt: 1, mx: "auto", minWidth: 200 }}
          >
            {loading ? <Loading specificDetails={{ size: 24 }} /> : "Authorize CLI"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Or <a href="/logout">sign out</a> to re-authenticate with different credentials.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ textAlign: "center" }}>
            <img
              style={{ width: "7em", margin: "0 auto" }}
              src={process.env.PUBLIC_URL + "/logo.png"}
              alt="logo"
            />
            <Typography variant="h5" sx={{ mt: 2 }}>
              {t.signin}
            </Typography>
            <Typography color="textSecondary">
              Authenticate CLI Application
            </Typography>
          </Box>

          {!authCode && (
            <Alert severity="error">
              Missing authentication code. Please try again with a valid authentication code.
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem"
            }}
            onKeyDown={onKeyPress}
            tabIndex={0}
          >
            <TextField
              label={t.username}
              name="username"
              value={form.username}
              onChange={onChangeTxt}
              size="small"
              required
              error={!!errors.username}
              helperText={errors.username}
              disabled={!authCode || loading}
            />
            
            <TextField
              label={t.password}
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={onChangeTxt}
              size="small"
              required
              error={!!errors.password}
              helperText={errors.password}
              disabled={!authCode || loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={onToggleShowPassword}
                      edge="end"
                      disabled={!authCode || loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              onClick={() => onLogin(SIMPLE_LOGIN, form)}
              disabled={!authCode || loading}
              sx={{ mt: 1 }}
            >
              {loading ? <Loading specificDetails={{ size: 24 }} /> : t.signin}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography color="textSecondary" sx={{ mb: 1 }}>
              Or continue with
            </Typography>
            
            <GoogleLogin
              onSuccess={(credentialResponse) => {
                onLogin(GOOGLE_LOGIN, credentialResponse);
              }}
              onError={() => {
                showError("Google login failed");
              }}
              disabled={!authCode || loading}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default withErrorHandling(CliLogin); 